/* eslint-disable security/detect-object-injection */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import classname from 'classnames';
import { toJS, observable, action, computed } from 'mobx';
import { observer } from 'mobx-react';
import { Toaster, toast } from 'sonner';
import { ModalProvider } from '../../components/modal';
import { ILxsCreateOrderState, ILxsCreateOrderViewProps, ILxsOrderData, IOrderHeader } from './lxs-create-order.data';
import {
    CREATE_ORDER_ADDITIONAL_INFORMATION_TEXT_ID,
    CREATE_ORDER_ITEM_PROP_CUSTOMER_ACCOUNT_NUMBER,
    CREATE_ORDER_ITEM_PROP_ENDCUSTOMERNUMBER,
    CREATE_ORDER_ITEM_PROP_NUMBER,
    CREATE_ORDER_ITEM_PROP_REQUESTEDDATE,
    CREATE_ORDER_NO_ENDCUSTOMER_VALUE,
    CREATE_ORDER_SAVE_CART_DELAY,
    ERROR_CODE_SERVER_ERROR_INDEX
} from '../../configuration';
import getSoldTosAction, { GetSoldTosInput, ISoldToTableData } from '../../actions/get-sold-tos.action';
import getShipTosAction, { GetShipTosInput, IShipToTableData } from '../../actions/get-ship-tos.action';
import getAllProductsAction, { GetAllProductsInput, IGetAllProductsResult } from '../../actions/get-all-products.action';
import { IGetCartData } from '../../actions/get-cart.action';
import getCartOrderNumberAction, { GetCartOrderNumberInput, IGetCartOrderNumberData } from '../../actions/get-cart-order-number.action';
import postCartAction, { IPostCartData, IPostCartItems, PostCartInput } from '../../actions/post-cart.action';
import postCreateOrderAction, {
    IPostCreateOrderData,
    IPostCreateOrderResponseData,
    PostCreateOrderInput
} from '../../actions/post-create-order.action';

/**
 *
 * LxsCreateOrder component
 * @extends {React.PureComponent<ILxsCreateOrderProps<ILxsCreateOrderData>>}
 */
@observer
class LxsCreateOrder extends React.PureComponent<ILxsCreateOrderViewProps, ILxsCreateOrderState> {
    @observable
    public fetchPrices: boolean = false;

    @action
    public setFetchPrices(fetchPrices: boolean) {
        this.fetchPrices = fetchPrices;
    }
    @observable
    public soldToData: ISoldToTableData[] | undefined;

    @action
    public setSoldToData(soldToData: any) {
        this.soldToData = toJS(soldToData.value);
    }
    @observable
    public shipToData: IShipToTableData[] | undefined;

    @action
    public setShipToData(shipToData: any) {
        this.shipToData = toJS(shipToData.value);
    }

    @observable
    public prevProducts: IPostCartItems[] | undefined;

    @action
    public setPrevProductsData(validProducts: IPostCartItems[]) {
        this.prevProducts = toJS(validProducts);
    }

    @observable
    public products: IGetAllProductsResult[] | undefined;

    @action
    public setProductsData(validProducts: IGetAllProductsResult[]) {
        this.products = toJS(validProducts);
    }

    @observable
    public productsHaveEndcustomers: boolean = false;

    @action
    public setProductsHaveEndcustomers(productsHaveEndcustomers: boolean) {
        this.productsHaveEndcustomers = productsHaveEndcustomers;
    }

    @observable
    public confirmedOrderData: IPostCreateOrderResponseData = {} as IPostCreateOrderResponseData;

    @action
    public setConfirmedOrderData(orderData: IPostCreateOrderResponseData) {
        this.confirmedOrderData = toJS(orderData);
    }

    constructor(props: ILxsCreateOrderViewProps) {
        super(props);
        this.state = {
            step: 0,
            orderData: {} as ILxsOrderData,
            processStarted: false,
            saveCartError: false,
            hasNoReorderItems: false
        };
    }

    @computed
    get cartTableData(): IGetCartData {
        const rawData = this.props.data.getCart;
        if (rawData?.error) {
            if (rawData.error.errorCode && Number(String(rawData.error.errorCode).charAt(0)) === ERROR_CODE_SERVER_ERROR_INDEX) {
                this._showToast(this.props.resources.globalErrorHeading, this.props.resources.globalErrorServiceNotAvailable);
            }
            return {} as IGetCartData;
        }
        return toJS(rawData.result);
    }

    private _getSoldTos = async () => {
        this.soldToData = undefined;
        const actionInput = new GetSoldTosInput();
        const soldToData = await getSoldTosAction(actionInput, this.props.context.actionContext);
        if (soldToData.error && Number(String(soldToData.error.errorCode).charAt(0)) === ERROR_CODE_SERVER_ERROR_INDEX) {
            this._showToast(this.props.resources.globalErrorHeading, this.props.resources.globalErrorServiceNotAvailable);
        } else {
            this.setSoldToData(soldToData);
        }
    };

    private _getShipTos = async (id: string) => {
        this.shipToData = undefined;
        const actionInput = new GetShipTosInput(id);
        const shipTos = await getShipTosAction(actionInput, this.props.context.actionContext);
        if (shipTos.error && Number(String(shipTos.error.errorCode).charAt(0)) === ERROR_CODE_SERVER_ERROR_INDEX) {
            this._showToast(this.props.resources.globalErrorHeading, this.props.resources.globalErrorServiceNotAvailable);
        } else {
            this.setShipToData(shipTos);
        }
    };

    private _getReorderData = async (orderNumber: string) => {
        const actionInput = new GetCartOrderNumberInput(orderNumber);
        const rawData = await getCartOrderNumberAction(actionInput, this.props.context.actionContext);
        if (rawData.error && Number(String(rawData.error.errorCode).charAt(0)) === ERROR_CODE_SERVER_ERROR_INDEX) {
            this._showToast(this.props.resources.globalErrorHeading, this.props.resources.globalErrorServiceNotAvailable);
        } else if (rawData.Items && rawData.Items.length === 0) {
            this.setState({ step: 0, hasNoReorderItems: true });
        } else {
            const data: IGetCartOrderNumberData = rawData ? toJS(rawData) : ({} as IGetCartOrderNumberData);
            await this._getProducts(data.SoldToId, data.ShipToId);

            /**
             * If the user go to another step, we need to make sure, that the soldTo and shipTo data is loaded
             */
            void this._getSoldTos();
            void this._getShipTos(data.SoldToId);
            this.setState({ processStarted: true });

            const products = ((data.Items.slice() as unknown) as IPostCartItems[]).map(product => ({
                ...product,
                Quantity: !!Number(product.Quantity) ? Number(product.Quantity).toFixed(0) : '',
                EndCustomerName: this.productsHaveEndcustomers ? product.EndCustomerName : CREATE_ORDER_NO_ENDCUSTOMER_VALUE,
                EndCustomerNumber: this.productsHaveEndcustomers ? product.EndCustomerNumber : CREATE_ORDER_NO_ENDCUSTOMER_VALUE
            }));

            this.setState({
                orderData: {
                    soldTo: {
                        ...data.SoldTo,
                        Id: data?.SoldToId,
                        RecordCreatedDate: '',
                        SalesAreas: [],
                        ExtensionProperties: []
                    },
                    shipTo: {
                        ...data.ShipTo,
                        Id: data.ShipToId,
                        RecordCreatedDate: '',
                        SalesAreas: [],
                        ExtensionProperties: []
                    },
                    products,
                    orderHeader: {
                        AdditionalInfo: data.AdditionalInfo,
                        DistributionChannel: data.DistributionChannel,
                        Division: data.Division,
                        DivisionText: data.DivisionText,
                        Incoterm1: data.Incoterm1,
                        SalesOrg: data.SalesOrg,
                        SalesOrgText: data.SalesOrgText,
                        // The SAP delivers the BusinessUnit as Division, so we need to map it to BusinessUnit
                        BusinessUnit: data.Division,
                        CustomerPONumber: data.Items.length > 0 ? data.Items[0].CustomerProductNumber : ''
                    }
                },
                step: 3
            });
        }
    };

    private _getProducts = async (soldTo: string, shipTo: string) => {
        this.products = undefined;
        const actionInput = new GetAllProductsInput(soldTo, shipTo);
        const allProducts = await getAllProductsAction(actionInput, this.props.context.actionContext);
        if (allProducts.error && Number(String(allProducts.error.errorCode).charAt(0)) === ERROR_CODE_SERVER_ERROR_INDEX) {
            this._showToast(this.props.resources.globalErrorHeading, this.props.resources.globalErrorServiceNotAvailable);
        } else {
            this.setProductsData(allProducts.Products);
            this.setProductsHaveEndcustomers(allProducts.EndCustomers);
        }
    };

    private _loadCartData = async () => {
        const cartData = this.props.data.getCart.result;
        await this._getSoldTos();
        const tempState = {} as ILxsCreateOrderState;

        if (cartData.SoldTo) {
            tempState.orderData = {
                ...tempState.orderData,
                soldTo: {
                    ...cartData.SoldTo,
                    Id: cartData.SoldToId,
                    RecordCreatedDate: '',
                    ExtensionProperties: [],
                    SalesAreas: cartData.SoldTo.SalesAreas as any
                }
            };

            await this._getShipTos(cartData.SoldToId);
        }

        if (cartData.ShipTo) {
            tempState.orderData = {
                ...tempState.orderData,
                shipTo: {
                    ...cartData.ShipTo,
                    Id: cartData.ShipToId,
                    RecordCreatedDate: '',
                    ExtensionProperties: [],
                    SalesAreas: cartData.ShipTo.SalesAreas as any
                }
            };

            await this._getProducts(cartData.SoldToId, cartData.ShipToId);
        }

        if (cartData.Items) {
            tempState.orderData = {
                ...tempState.orderData,
                products: (cartData.Items.slice() as unknown) as IPostCartItems[]
            };
        }

        if (cartData.CustomerPo) {
            tempState.orderData.orderHeader = {
                ...tempState.orderData.orderHeader,
                CustomerPONumber: cartData.CustomerPo
            };
        }

        if (cartData.AdditionalInfo) {
            tempState.orderData.orderHeader = {
                ...tempState.orderData.orderHeader,
                AdditionalInfo: cartData.AdditionalInfo
            };
        }

        if (cartData.DistributionChannel) {
            tempState.orderData.orderHeader = {
                ...tempState.orderData.orderHeader,
                DistributionChannel: cartData.DistributionChannel
            };
        }

        if ((cartData.Division && cartData.SalesOrg && cartData.SalesOrgText, cartData.DivisionText)) {
            tempState.orderData.orderHeader = {
                ...tempState.orderData.orderHeader,
                Division: cartData.Division,
                DivisionText: cartData.DivisionText,
                SalesOrg: cartData.SalesOrg,
                SalesOrgText: cartData.SalesOrgText
            };
        }

        if (cartData.Incoterm1) {
            tempState.orderData.orderHeader = {
                ...tempState.orderData.orderHeader,
                Incoterm1: cartData.Incoterm1
            };
        }

        if (cartData.OrderType) {
            tempState.orderData.orderHeader = {
                ...tempState.orderData.orderHeader,
                OrderType: cartData.OrderType
            };
        }

        if (cartData.Step) {
            tempState.step = cartData.Step;
        }

        tempState.processStarted = true;
        this.setState(tempState);
    };

    private _saveCartDebouncer: () => () => Promise<void> = (): (() => Promise<void>) => {
        let called: boolean = false;

        return async () => {
            if (!called) {
                called = true;
                setTimeout(async () => {
                    await this._saveCart().then(res => {
                        if (((res as unknown) as any)?.Exception) {
                            this.setState({ saveCartError: true });
                        } else {
                            const { orderData } = this.state;
                            // if savecart does fail because of missmatching conditions, last correct response will be used to display
                            try {
                                const products = (res.slice() as unknown) as IPostCartItems[];
                                orderData.products = products;
                                this.setFetchPrices(false);
                            } catch {
                                let products = toJS(res);
                                if (!Array.isArray(products)) {
                                    products = Object.values(products);
                                }
                                products = products as IPostCartItems[];
                                orderData.products = products;
                                this.setFetchPrices(false);
                            }
                        }
                    });
                    called = false;
                }, CREATE_ORDER_SAVE_CART_DELAY);
            }
        };
    };

    private _saveCartHandler: () => Promise<void> = this._saveCartDebouncer();

    private _saveCart = async () => {
        try {
            const { orderData, step } = this.state;
            const { soldTo, shipTo, products } = orderData;
            const orderHeader = orderData.orderHeader;
            const Items = products?.map((product, index) => ({
                CustomerAccountNumber: product.CustomerAccountNumber,
                CustomerProductNumber: product.CustomerProductNumber,
                EndCustomerName: product.EndCustomerName,
                EndCustomerNumber: product.EndCustomerNumber,
                PriceAmount: product.PriceAmount,
                PriceFactor: product.PriceFactor,
                PriceUnit: product.PriceUnit,
                PriceCurrency: product.PriceCurrency,
                ProductName: product.ProductName,
                ProductNumber: product.ProductNumber,
                RequestedDate: product.RequestedDate,
                UnitOfMeasure: product.UnitOfMeasure,
                Quantity: product.Quantity,
                Number: index * 10
            }));

            const cartData: IPostCartData = {
                SalesOrg: orderHeader?.SalesOrg ?? '',
                AdditionalInfo: orderHeader?.AdditionalInfo ?? '',
                CustomerPo: orderHeader?.CustomerPONumber ?? '',
                DistributionChannel: orderHeader?.DistributionChannel ?? '',
                Division: orderHeader?.Division ?? '',
                Incoterm1: orderHeader?.Incoterm1 ?? '',
                OrderType: orderHeader?.OrderType ?? '',
                SoldToId: soldTo?.Id ?? null,
                ShipToId: shipTo?.Id ?? null,
                Items: Items ?? [],
                Step: step,
                PriceFetch: orderHeader?.PriceFetch ?? false
            };

            const actionInput = new PostCartInput(cartData);

            const postCartResponse = await postCartAction(actionInput, this.props.context.actionContext).then(res => {
                this.setPrevProductsData({ ...cartData.Items }); // Update prevProducts after state update
                if (res === null) {
                    return this.prevProducts;
                }
                return toJS(res.Cart.Items);
            });
            return postCartResponse;
        } catch (error) {
            console.error('Error saving cart', error);
            return error;
        }
    };

    private _collectOrderData = (): IPostCreateOrderData => {
        const { orderData } = this.state;
        const { soldTo, shipTo, products, orderHeader } = orderData;
        return {
            Language: this.props.context.request.locale,
            Order: {
                Incoterm1: orderHeader?.Incoterm1 ?? '',
                PurchaseOrderNumbExt: orderHeader?.CustomerPONumber ?? '',
                SalesOrg: orderHeader?.SalesOrg ?? '',
                DistributionChannel: orderHeader?.DistributionChannel ?? '',
                Division: orderHeader?.Division ?? '',
                SoldTo: soldTo?.Id ?? '',
                ShiptTo: shipTo?.Id ?? '',
                OrderLines:
                    products?.map(product => ({
                        ProductNumber: product.ProductNumber,
                        RequestedDate: product.RequestedDate,
                        RequestedQty: product.Quantity,
                        Unit: product.UnitOfMeasure,
                        EndCustomer: product.EndCustomerNumber
                    })) ?? [],
                HeaderTexts: [
                    {
                        TextId: CREATE_ORDER_ADDITIONAL_INFORMATION_TEXT_ID,
                        Value: orderHeader?.AdditionalInfo ?? ''
                    }
                ]
            }
        };
    };

    private _placeOrder = async (placeOrderData: IPostCreateOrderData) => {
        const actionInput = new PostCreateOrderInput(placeOrderData);
        const response = await postCreateOrderAction(actionInput, this.props.context.actionContext);
        if (!!response['@odata.context']) {
            this.setConfirmedOrderData(response);
            this.setState({ step: 6 });
        }
    };

    private _showToast = (heading: string, description: string) => {
        toast.error(
            <div className='toast' role='alert'>
                <div className='toast-header'>
                    <h4>{heading}</h4>
                    <button className='toast-close' onClick={() => toast.dismiss()}></button>
                </div>
                <div className='toast-body'>
                    <span>{description}</span>
                </div>
            </div>
        );
    };

    public componentDidMount(): void {
        /**
         * If the user wants to reorder an order, we need to load the order data
         */
        const { orderNumber = '' } = this.props.context.actionContext.requestContext.query ?? {};
        if (!!orderNumber) {
            this.setState({ step: 0 });
            void this._getReorderData(orderNumber);
        }

        /**
         * If there is no cart data or an error occured,
         * we need to load the soldTo data to be able to create a new order
         */
        if (!this.props.data.getCart?.result?.['@odata.context']) {
            this.setState({ step: 1, processStarted: true });
            void this._getSoldTos();
        }
    }

    public componentDidUpdate(_: Readonly<ILxsCreateOrderViewProps>, prevState: Readonly<ILxsCreateOrderState>): void {
        /**
         * If the user goes back to the first two steps,
         * we need to clear the products to avoid conflicts
         */
        if (prevState.step > 2 && this.state.step <= 2) {
            this.setState({ orderData: { ...this.state.orderData, products: [] } });
        }

        /**
         * To improve the user experience, we need to scroll to the top of the page
         */
        if (prevState.step !== this.state.step) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    public render(): JSX.Element | null {
        const {
            config: { heading, pageSize }
        } = this.props;

        const loadSoldTos = () => {
            this.setState({ processStarted: true, step: 1 });
            void this._getSoldTos();
        };
        const setCartData = () => {
            void this._loadCartData();
        };

        const setSoldTo = (soldTo: ISoldToTableData) => {
            if (soldTo && soldTo.Id) {
                void this._getShipTos(soldTo.Id);
            }
            this.setState({ step: 2 });
            const newOrderData = { ...this.state.orderData, soldTo };
            delete newOrderData.shipTo;
            this.setState({ orderData: newOrderData }, () => {
                void this._saveCartHandler();
            });
        };
        const setShipTo = (shipTo: IShipToTableData) => {
            if (shipTo.Id) {
                void this._getProducts(this.state.orderData.soldTo.Id, shipTo.Id);
            }
            this.setState({ step: 3 });
            this.setState({ orderData: { ...this.state.orderData, shipTo } }, () => {
                void this._saveCartHandler();
            });
        };

        /**
         * Check for all changes between the prev and the current state.
         * Exclude the Number value as it only represents the position in the array.
         * Do not check for empty endcustomernumbers.
         * @param prev The previously set product
         * @param current The changed product
         * @returns an array of all changes between prev and current
         */
        const compareProducts = (prev: IPostCartItems, current: IPostCartItems): any[] => {
            const changes = [];
            for (const key in prev) {
                if (prev[key as keyof IPostCartItems] !== current[key as keyof IPostCartItems]) {
                    if (key === CREATE_ORDER_ITEM_PROP_NUMBER || key === CREATE_ORDER_ITEM_PROP_CUSTOMER_ACCOUNT_NUMBER) {
                        continue;
                    }
                    if (key === CREATE_ORDER_ITEM_PROP_ENDCUSTOMERNUMBER && current[key as keyof IPostCartItems]) {
                        continue;
                    }
                    if (key === CREATE_ORDER_ITEM_PROP_REQUESTEDDATE) {
                        // We need to convert each date to ISOString, because of multiple styles to save them.
                        const currentDate = current[key as keyof IPostCartItems];
                        if (currentDate instanceof Date && prev[key as keyof IPostCartItems] === currentDate.toISOString()) {
                            continue;
                        }
                    }
                    changes.push({ key, prevValue: prev[key as keyof IPostCartItems], currentValue: current[key as keyof IPostCartItems] });
                }
            }
            return changes;
        };

        const setProducts = (products: IPostCartItems[]) => {
            const prevProducts = this.prevProducts ? JSON.parse(JSON.stringify(this.prevProducts)) : [];
            const prevProductsArray = Object.values(prevProducts as IPostCartItems);
            let fetchPrices = false;

            if (prevProductsArray && prevProductsArray.length > 0) {
                // Set fetchPrices, if products have been added.
                // TODO: Check for removed products
                if (prevProductsArray.length !== products.length) {
                    fetchPrices = true;
                } else {
                    products.map((currentProduct: IPostCartItems) => {
                        const compareProduct = prevProductsArray.find((prevProduct: IPostCartItems) => {
                            return (
                                prevProduct.ProductNumber === currentProduct.ProductNumber && prevProduct.Number === currentProduct.Number // Search for the exact product in the previously saved products
                            );
                        });
                        if (currentProduct && compareProduct) {
                            const changes = compareProducts(compareProduct, currentProduct); // Check for any changes between the current and the last state
                            if (changes.length > 0) {
                                fetchPrices = true;
                            }
                        }
                    });
                }
            } else {
                fetchPrices = true;
            }

            if (fetchPrices) {
                this.setFetchPrices(true);
            }

            this.setState(
                prevState => ({
                    orderData: {
                        ...prevState.orderData,
                        orderHeader: {
                            ...prevState.orderData.orderHeader,
                            PriceFetch: fetchPrices
                        },
                        products
                    }
                }),
                () => {
                    void this._saveCartHandler();
                }
            );
        };
        const setOrderHeader = (orderHeader: IOrderHeader) => {
            this.setState(
                prevState => ({
                    orderData: {
                        ...prevState.orderData,
                        orderHeader: orderHeader
                    }
                }),
                () => {
                    void this._saveCartHandler();
                }
            );
        };

        const placeOrder = () => {
            const placeOrderData = this._collectOrderData();
            void this._placeOrder(placeOrderData);
        };

        const setStep = (step: number) => {
            this.setState({ step }, () => {
                if (step > 3 && step < 6) {
                    void this._saveCartHandler();
                }
            });
        };

        const viewProps = {
            ...this.props,
            pageSize: pageSize || 10,
            moduleProps: { moduleProps: this.props, className: classname('lxs-create-order', this.props.config.className) },
            header: {
                headerProps: { className: 'lxs-create-order__header' },
                heading: heading && heading.text && (
                    <Msdyn365.Text
                        {...heading}
                        className='lxs-create-order__heading'
                        text={heading.text}
                        tag={heading.tag || 'h2'}
                        editProps={{ onEdit: this.handleLxsCreateOrderHeadingChange, requestContext: this.props.context.request }}
                    />
                )
            },
            cartData: this.cartTableData,
            soldToData: toJS(this.soldToData),
            shipToData: toJS(this.shipToData),
            productsData: toJS(this.products),
            step: this.state.step,
            hasNoReorderItems: this.state.hasNoReorderItems,
            orderData: this.state.orderData,
            processStarted: this.state.processStarted,
            saveCartError: this.state.saveCartError,
            confirmedOrderData: this.confirmedOrderData,
            productsHaveEndcustomers: this.productsHaveEndcustomers,
            fetchPrices: this.fetchPrices,
            setStep: (step: number) => setStep(step),
            loadSoldTos: () => loadSoldTos(),
            setCartData: () => setCartData(),
            setSoldTo: (soldTo: ISoldToTableData) => setSoldTo(soldTo),
            setShipTo: (shipTo: IShipToTableData) => setShipTo(shipTo),
            setProducts: (products: IPostCartItems[]) => setProducts(products),
            setOrderData: (orderData: any) => this.setState({ orderData }),
            placeOrder: () => placeOrder(),
            setOrderHeader: setOrderHeader
        };
        return (
            <>
                <Toaster
                    richColors
                    position='top-right'
                    toastOptions={{
                        classNames: {
                            default: 'toast-light',
                            error: 'toast-danger',
                            success: 'toast-success',
                            warning: 'toast-warning',
                            info: 'toast-info'
                        }
                    }}
                />
                <ModalProvider props={this.props}>{this.props.renderView(viewProps) as React.ReactElement}</ModalProvider>
            </>
        );
    }

    public handleLxsCreateOrderHeadingChange = (event: Msdyn365.ContentEditableEvent) =>
        (this.props.config.heading!.text = event.target.value);
}

export default LxsCreateOrder;
